/**
 * These functions control loading and opening of a Qualtive feedback script.
 * We use this because we do not want to load the Qualtive script on each page load.
 */
import initData from 'init-data';
import GdprSettings from 'tradera-utils/gdpr-settings';

/**
 * Returns true if Qualtive widgets is enabled.
 */
const isQualtiveEnabled = () => {
    return (
        initData &&
        initData.featureSwitches &&
        initData.featureSwitches['qualtive-feedback'] &&
        GdprSettings.isFunctionalEnabled()
    );
};

/**
 * Adds the Qualtive script to the page if not there
 */
const addScriptToDom = () => {
    if (!isQualtiveEnabled()) {
        return;
    }
    const dataAttributeName = 'data-qualtive-script';
    if (document.querySelector(`[${dataAttributeName}]`)) {
        return;
    }
    const js = document.createElement('script');
    js.setAttribute(dataAttributeName, '1');
    js.setAttribute('type', 'text/javascript');
    js.setAttribute('src', 'https://static.qualtive.io/client/web.v1.js');
    document.getElementsByTagName('head')[0].appendChild(js);
    loadQualtiveScript();
};

/**
 * Loads Qualtive script
 */
const loadQualtiveScript = () => {
    if (!isQualtiveEnabled()) {
        return;
    }
    let attemptsLeft = 100;
    const timeBetweenAttempts = 50;
    const interval = setInterval(function() {
        if (window.qualtive) {
            clearInterval(interval);
        } else if (--attemptsLeft < 1) {
            clearInterval(interval);
        }
    }, timeBetweenAttempts);
};

const mapLocale = languageCodeIso2 => {
    const LOCALES = {
        sv: 'sv-se',
        en: 'en-us',
        da: 'da-dk',
        de: 'de-de'
    };

    return languageCodeIso2 in LOCALES
        ? LOCALES[languageCodeIso2]
        : LOCALES['sv'];
};

/**
 * Opens Qualtive with a certain survey/question.
 */
const openQualtiveModal = (survey, translator) => {
    if (!isQualtiveEnabled() || !survey) {
        return;
    }
    addScriptToDom();
    let attemptsLeft = 100;
    const timeBetweenAttempts = 50;
    const { languageCodeIso2 } = initData;
    let qualtive;
    const interval = setInterval(function() {
        qualtive = window.qualtive;
        if (qualtive) {
            let payload = {
                title: translator(survey.title),
                locale: mapLocale(languageCodeIso2),
                supportURL: survey.supportURL,
                darkMode: 'never'
            };
            const { memberId } = initData;
            if (memberId) {
                payload = { ...payload, user: { id: memberId } };
            }
            setTimeout(() => {
                qualtive.present(survey.survey, payload);
            }, 100);
            clearInterval(interval);
        } else if (--attemptsLeft < 1) {
            clearInterval(interval);
        }
    }, timeBetweenAttempts);
};

const surveys = {
    general: {
        survey: 'tradera/hur-tycker-du-att-det-fungerar-att-anvanda-tradera',
        title: 'qualtiveTitle_generalFeedback',
        supportURL: 'https://info.tradera.com/'
    },
    selectShipping: {
        survey: 'tradera/valja-frakt-hur-fungerar-det-for-dig',
        title: 'qualtiveTitle_selectShipping'
    },
    messaging: {
        survey:
            'tradera/hur-tycker-du-att-det-fungerar-att-skicka-och-ta-emot-meddelanden',
        title: 'qualtiveTitle_messaging'
    },
    language: {
        survey: 'tradera/english-translation---rate-your-experience',
        title: 'qualtiveTitle_translations'
    },
    paynova: {
        survey: 'tradera/hur-tycker-du-att-paynova-fungerar',
        title: 'qualtiveTitle_paynova'
    },
    automaticSuggestion: {
        survey: 'tradera/var-de-automatiska-forslagen-relevanta-for-dig',
        title: 'qualtiveTitle_automaticsuggestions'
    },
    cardVerification: {
        survey: 'tradera/vad-fick-dig-att-avbryta-kortverifieringen',
        title: 'qualtiveTitle_cardverification'
    },
    bankId: {
        survey: 'tradera/bankid',
        title: 'qualtiveTitle_bankid'
    },
    myTraderaBuyerActiveItems: {
        survey:
            'tradera/hur-tycker-du-det-fungerar-att-filtrera-soka-och-sortera-i-listan-for-aktiva-annonser',
        title: 'qualtiveTitle_mytraderabuyeractiveitems'
    },
    myTraderaBuyerPurchases: {
        survey:
            'tradera/hur-tycker-du-det-fungerar-att-filtrera-soka-och-sortera-i-listan-for-mina-kop',
        title: 'qualtiveTitle_mytraderabuyerpurchases'
    },
    myTraderaBuyerItemsLost: {
        survey:
            'tradera/hur-tycker-du-det-fungerar-att-filtrera-soka-och-sortera-i-listan-for-mina-ej-vunna',
        title: 'qualtiveTitle_mytraderabuyeritemslost'
    },
    myTraderaSellerActiveItems: {
        survey:
            'tradera/hur-tycker-du-det-fungerar-att-filtrera-soka-och-sortera-i-listan-for-aktiva-annonser-2',
        title: 'qualtiveTitle_mytraderaselleractiveitems'
    },
    myTraderaSellerSoldItems: {
        survey:
            'tradera/hur-tycker-du-det-fungerar-att-filtrera-soka-och-sortera-i-listan-for-salda-annonser',
        title: 'qualtiveTitle_mytraderasellersolditems'
    },
    myTraderaSellerUnsoldItems: {
        survey:
            'tradera/hur-tycker-du-det-fungerar-att-filtrera-soka-och-sortera-i-listan-for-osalda-annonser',
        title: 'qualtiveTitle_mytraderasellerunsolditems'
    }
};

export { openQualtiveModal, isQualtiveEnabled, surveys };
